import { Column, Row } from "components/Layout";
import DashboardSaleCard from "containers/UserDashboard/Cards/DashboardSaleCard";
import { Paper } from "components/Paper";
import React from "react";
import { useSelector } from "react-redux";
import { getSetting, selectSaleIdsGroupedByPastAndFuture } from "selectors";
import { Settings } from "constants/settings";
import { EmptyDashboardIllustration } from "containers/UserDashboard/Cards/EmptyStateIllustration";
import styled from "styled-components/macro";
import { AddSaleInEmptyState } from "components/Button/AddSaleButton";

const SalesRowContainerWrapper = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.space[3]}px;
`,
);

interface SaleRowsContainerProps {
  title: string;
  saleIds: number[];
  emptyStateText: string;
}

const SaleRowsContainer = ({
  title,
  saleIds,
  emptyStateText,
}: SaleRowsContainerProps) => (
  <SalesRowContainerWrapper>
    <Row marginVertical={2}>
      <span
        style={{
          fontSize: "16px",
        }}
      >
        {title}
      </span>
    </Row>
    {saleIds.length > 0 ? (
      saleIds.map(saleId => <DashboardSaleCard saleId={saleId} key={saleId} />)
    ) : (
      <Paper backgroundColor="white" isSquare={false}>
        <Row alignCenter flex={false} padding={2} textAlignCenter>
          {emptyStateText}
        </Row>
      </Paper>
    )}
  </SalesRowContainerWrapper>
);

interface MobileUserDashboardProps {
  canCreateSales: boolean;
}

export const CardUserDashboard = ({
  canCreateSales,
}: MobileUserDashboardProps): React.JSX.Element => {
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));

  const [pastSaleIds, futureSaleIds] = useSelector<RootState, number[][]>(
    selectSaleIdsGroupedByPastAndFuture,
  );

  const hasPastSales = pastSaleIds.length > 0;
  const hasFutureSales = futureSaleIds.length > 0;
  const hasNoTotalSales = !hasPastSales && !hasFutureSales;

  let emptyStateText;
  if (!hasPastSales && !hasFutureSales) {
    emptyStateText = (
      <>
        {allSalesLoaded ? "You haven't added any sales." : null}
        <AddSaleInEmptyState
          buttonText="Add A Sale Now."
          canCreateSale={canCreateSales}
          useStyledButton={false}
        />
      </>
    );
  } else if (!hasPastSales && hasFutureSales) {
    emptyStateText = <> You don&apos;t have any past sales.</>;
  } else if (hasPastSales && !hasFutureSales) {
    emptyStateText = (
      <>
        You don&apos;t have any upcoming sales.
        <AddSaleInEmptyState
          buttonText="Add A Sale Now."
          canCreateSale={canCreateSales}
          useStyledButton={false}
        />
      </>
    );
  }

  return (
    <Column paddingHorizontal={3} marginVertical={3}>
      {!hasNoTotalSales ? (
        <>
          <SaleRowsContainer
            title="Now and Future"
            saleIds={futureSaleIds}
            emptyStateText={emptyStateText}
          />
          <SaleRowsContainer
            title="Past"
            saleIds={pastSaleIds}
            emptyStateText={emptyStateText}
          />
        </>
      ) : (
        <Paper backgroundColor="gray95" isSquare={false}>
          <Row paddingVertical={3}>
            <EmptyDashboardIllustration />
          </Row>
          <Row alignCenter flex={false} textAlignCenter fullWidth>
            {emptyStateText}
          </Row>
        </Paper>
      )}
    </Column>
  );
};
